import { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';

import { cn } from '@/lib/utils';

type BaseProps = {
  label?: string;
  hint?: string;
  error?: string;
};

export interface NbFieldProps
  extends BaseProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {}

export const NbField = forwardRef<HTMLInputElement, NbFieldProps>(
  ({ label, hint, error, className, id, ...rest }, ref) => (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={id}
          className="text-[11px] font-bold uppercase tracking-[0.08em]"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        className={cn(
          'w-full px-3 py-2 text-[14px] font-medium',
          'border-2 focus:outline-none',
          error ? 'border-red-500' : 'border-[var(--nb-ink)]',
          className,
        )}
        style={{ background: 'var(--nb-paper)', color: 'var(--nb-ink)', borderRadius: 'var(--nb-radius)' }}
        {...rest}
      />
      {hint && !error && (
        <span
          className="text-[10px] opacity-60"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {hint}
        </span>
      )}
      {error && (
        <span
          className="text-[11px] font-semibold"
          style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
        >
          {error}
        </span>
      )}
    </div>
  ),
);
NbField.displayName = 'NbField';

export interface NbTextareaProps extends BaseProps, TextareaHTMLAttributes<HTMLTextAreaElement> {}

export const NbTextarea = forwardRef<HTMLTextAreaElement, NbTextareaProps>(
  ({ label, hint, error, className, id, rows = 4, ...rest }, ref) => (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={id}
          className="text-[11px] font-bold uppercase tracking-[0.08em]"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        className={cn(
          'w-full px-3 py-2 text-[14px] font-medium',
          'border-2 focus:outline-none',
          error ? 'border-red-500' : 'border-[var(--nb-ink)]',
          className,
        )}
        style={{ background: 'var(--nb-paper)', color: 'var(--nb-ink)', borderRadius: 'var(--nb-radius)' }}
        {...rest}
      />
      {hint && !error && (
        <span
          className="text-[10px] opacity-60"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {hint}
        </span>
      )}
      {error && (
        <span
          className="text-[11px] font-semibold"
          style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
        >
          {error}
        </span>
      )}
    </div>
  ),
);
NbTextarea.displayName = 'NbTextarea';
